<template>
  <div ref="rootRef" :class="[menuAlignment, { show: isOpen }]" class="dropdown">
    <ButtonElement
      class="btn--ms btn--neutral"
      :class="buttonclass"
      :is-disable="disabled"
      :aria-expanded="isOpen"
      @click="onTriggerClick"
    >
      <template v-if="type === 'button'">
        <div v-if="svg || icon && !iconRight" :class="['icon', { 'icon-no-margin': !name && (svg || icon) }]">
          <img v-if="svg" :src="assetUrl(`assets/${svg}`)"/>
          <LucideIcon v-if="icon && !iconRight" :icon="icon"/>
        </div>
        <span v-if="name">{{ name }} </span>
        <LucideIcon v-if="iconRight" :icon="icon"/>
      </template>
      <div v-if="type === 'buttonselect'" class="icon-name">
        <div style="display:flex;">
          <div v-if="svg || icon" class="icon">
            <img v-if="svg" :src="assetUrl(`assets/${svg}`)"/>
            <LucideIcon v-if="icon" :icon="icon"/>
          </div>
          <span v-if="name">{{ name }}</span>
        </div>
        <div class="loading-ai">
          <span class="loader"></span>
        </div>
        <ChevronDown class="icon-angle" aria-hidden="true" />
      </div>

    </ButtonElement>

    <ul id="dropdownMenu" class="dropdown-menu opened-dropdown-menu" :class="{ show: isOpen }">
      <div class="dropdown-menu-box animation">
        <div class="dropdown-menu-head" v-if="title">{{ title }}</div>
        <template v-for="(option, index) in options" :key="index">
          <li
              :class="{ 'disabled-li': option.disabled,  'dropdown-has-submenu': !!option.subOptions}"
              @click="onOptionSelect(option, $event)"
          >
            <a
                v-if="option.name"
                class="dropdown-item "
                @click="(!option.subOptions || !option.disabled) && setSelectedOption(index);"
            >
              <div v-if="option.checkbox" class="checkbox">
                <CheckboxElement
                    :disabled="option.disabled"
                    :is-checked="option.isChecked"
                    name="checkbox"
                    v-model="option.isChecked"/>
              </div>

              <div v-if="option.radio" class="radio">
                <RadioElement
                    :disabled="option.disabled"
                    :name="option.name ?? ''"
                    :value="option.value ?? ''"
                    :model-value="modelValue ?? ''"
                    @update:model-value="(v: unknown) => emit('update:modelValue', v)"
                    :id="'radio-' + index"
                />
              </div>

              <div v-if="option.icon" class="icon">
                <LucideIcon :icon="option.icon"/>
              </div>
              <div v-if="option.svg" class="icon">
                <img v-if="option.svg" :src="assetUrl(`assets/${option.svg}`)"/>
              </div>
              <div v-if="option.upgrade" class="icon">
                <UpgradeElement v-if="option.upgrade" size="sm" style="position: relative; left: -2px;"/>
              </div>

              <NewLabelElement v-if="option.newLabel" top-position="12px" right-position="-20px"
                               alignment="left"/>

              <div
                  class="content"
                  @click="!option.switch && option.callback ? option.callback($event) : null"
              >
                <h6>{{ option.name }}</h6>
                <p v-if="option.content">{{ option.content }}</p>
              </div>
              <div v-if="!!option.subOptions" class="item-arrow">
                <ChevronRight aria-hidden="true" />
              </div>
            </a>
            <ul
                v-if="!!option.subOptions"
                class="dropdown-menu submenu"
                :class="option.subOptionsAlignment === 'left' ? 'submenu-left' : 'submenu-right'">
              <div class="dropdown-menu-box animation">
                <li><h6 class="dropdown-menu-head">{{ option.subOptions?.title }}</h6></li>
                <template v-for="(subItem, index) in option.subOptions.options" :key="index">
                  <li v-if="subItem.name" :class="{ 'disabled-li': subItem.disabled }">
                    <a @click="subItem.callback?.($event)" class="dropdown-item"
                       :class="{'active': subItem.isActive}">
                      <div v-if="subItem.icon" class="icon">
                        <LucideIcon :icon="subItem.icon"/>
                      </div>
                      <div v-if="subItem.svg" class="icon">
                        <img v-if="subItem.svg" :src="assetUrl(`assets/${subItem.svg}`)"/>
                      </div>
                      <NewLabelElement v-if="subItem.newLabel" top-position="12px" right-position="-20px"
                                       alignment="left"/>
                      <div class="content">
                        <span>{{ subItem.name }}</span>
                        <p v-if="subItem.content">{{ subItem.content }}</p>
                      </div>
                      <div class="item-arrow"></div>
                    </a>
                  </li>
                  <li v-if="subItem.hasDivider">
                    <hr class="dropdown-divider">
                  </li>
                </template>
              </div>
            </ul>

            <div v-if="option.headline" class="dropdown-menu-head">{{ option.headline }}</div>

            <div v-if="option.info" class="info">
              <span>{{ option.info }}</span>
              <span>{{ option.time }}</span>
            </div>
          </li>
          <li v-if="option.hasDivider">
            <hr class="dropdown-divider">
          </li>
        </template>
      </div>
    </ul>

  </div>
</template>

<script setup lang="ts">
import { ChevronDown, ChevronRight } from '@lucide/vue'
import {getCurrentInstance, ref} from 'vue';
import type {DropdownNs} from '@shared/types';
import {useDropdown} from '@shared/composables/use-dropdown';
import ButtonElement from '@shared/components/elements/button/button.element.vue';
import CheckboxElement from '@shared/components/elements/checkbox/checkbox.element.vue';
import RadioElement from '@shared/components/elements/radio/radio.element.vue';
import NewLabelElement from '@shared/components/elements/labels/new-label.element.vue';
import UpgradeElement from '@shared/components/elements/labels/upgrade.element.vue';

const props = withDefaults(defineProps<{
  value?: string;
  title?: string;
  options: DropdownNs.DropdownOptionsItem[];
  type?: 'button' | 'buttonselect';
  buttonclass?: string;
  name?: string;
  buttonClass?: string;
  icon?: string;
  iconRight?: boolean;
  iconType?: string;
  svg?: string;
  menuAlignment?: string;
  info?: string;
  time?: string;
  isChecked?: boolean;
  disabled?: boolean;
  modelValue?: string;
}>(), {
  type: 'button',
  name: '',
  buttonClass: 'btn-grey',
  iconType: 'fal',
  disabled: false,
});

const emit = defineEmits<{
  (e: 'opened'): void;
  (e: 'switched'): void;
  (e: 'change', item: DropdownNs.DropdownOptionsItem): void;
  (e: 'update:value', value: string | undefined): void;
  (e: 'update:modelValue', value: unknown): void;
}>();

const $image = (getCurrentInstance()?.proxy as unknown as {$image?: (url: string) => string})?.$image;
const assetUrl = (url: string): string => $image?.(url) ?? url;

const {isOpen, rootRef, toggle, close} = useDropdown();

const selectedItem = ref<DropdownNs.DropdownOptionsItem | undefined>(
  props.options.find(item => item.value === props.value),
);

function onTriggerClick(): void {
  toggle();
  emit('opened');
}

function setSelectedOption(key: number): void {
  const option = props.options[key];
  if (!option) return;
  selectedItem.value = option;
  emit('update:value', option.value);
  emit('change', option);
}

function onOptionSelect(option: DropdownNs.DropdownOptionsItem, event: Event): void {
  if (!option.closeOnSelect) {
    event.stopPropagation();
  } else {
    close();
  }

  if ((option.switch || option.checkbox) && !option.disabled) {
    triggerSwitchOption(option);
  }

  if (option.radio && !option.disabled) {
    emit('update:modelValue', option.value);
  }
}

function triggerSwitchOption(option: DropdownNs.DropdownOptionsItem): void {
  option.isChecked = !option.isChecked;
  if (option.callback) option.callback(option.isChecked);
}
</script>

<style lang="scss" scoped>

.dropdown {

  &.dropdown-ai-features {
    .dropdown-menu {
      width: 255px;
      transform: none !important;
      top: 36px !important;
      left: 5px !important;
    }
  }

  &.dropdown-ai-description {
    .dropdown-menu {
      transform: none !important;
      top: 36px !important;
      left: 5px !important;
    }
  }
}

.dropdown-menu {

  .dropdown-item {
    position: relative;
  }

  .dropdown-has-submenu {
    &:hover > .submenu {
      display: block;
    }

    position: relative;

    .submenu {
      right: -195px;
      top: -5px;

      &.submenu-right {
        right: -195px;
        left: initial;
      }

      &.submenu-left {
        top: 5px;
        left: -95px;
        right: initial;
        max-width: 230px;
      }

      .dropdown-item {
        cursor: pointer;

        &.active {
          .item-arrow {
            background-image: url("data:image/svg+xml,%3Csvg class='svg-inline--fa fa-check fa-w-14' aria-hidden='true' focusable='false' data-prefix='fal' data-icon='check' role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'%3E%3Cpath class='' fill='currentColor' d='M413.505 91.951L133.49 371.966l-98.995-98.995c-4.686-4.686-12.284-4.686-16.971 0L6.211 284.284c-4.686 4.686-4.686 12.284 0 16.971l118.794 118.794c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-11.314-11.314c-4.686-4.686-12.284-4.686-16.97 0z'%3E%3C/path%3E%3C/svg%3E");
            display: inline-flex;
            width: 10px;
            height: 10px;
            background-repeat: no-repeat;
            margin-right: var(--space-0);
          }
        }
      }
    }
  }

  .content {
    padding-right: var(--space-12);

    h6 {
      display: flex;
      align-items: center;
      gap: var(--space-6);;
    }
  }

  .icon {
    font-size: var(--fs-16) !important;
  }

  .checkbox {
    width: 28px;
    margin-right: var(--space-12);
  }

  .radio {
    width: 23px;
    margin-right: var(--space-12);
  }
}

.dropdown-disabled {
  .btn-with-ai {
    opacity: 1;
    background-color: var(--light-200) !important;
    border-color: var(--light-200) !important;
    color: var(--dark-200) !important;

    &:hover,
    &:focus,
    &:active {
      background-color: var(--light-200) !important;
      border-color: var(--light-200) !important;
      color: var(--dark-200) !important;
    }

    .loading-ai {
      display: flex;
    }

    .icon-angle {
      display: none;
    }

    .icon-angle {
      margin-left: var(--space-12) !important;
    }
  }
}

.loading-ai {
  margin-left: var(--space-12);
  width: 30px;
  margin-right: var(--space-negative-6);
  display: none;

  .loader {
    width: 4px;
    height: 4px;
    border-radius: $br-circle;
    display: block;
    margin: 0 auto;
    position: relative;
    color: var(--text-dark);
    box-sizing: border-box;
    animation: animloader 1s linear infinite alternate;
  }
}

.mr-5 {
  margin-right: 5px !important;
}

.width25 {
  width: 25px;
  height: 40px !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

.iconBox {
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center
}

.icon-name {
  display: flex;
  justify-content: center;
  align-items: center;

  .icon-angle {
    margin-left: 5px
  }
}

.actionBar {
  .dropdown-menu {
    inset: 30px auto auto 5px !important;
  }
}

.actionbar {
  .btn-default {
    background-color: var(--light-300);
    color: var(--text-dark);
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    line-height: 20px;
    padding: 5px;
    margin-right: 3px;

    &:hover {
      background-color: var(--light-100) !important;
      color: var(--dark-200) !important;
    }
  }
}

.btn-actionbar {
  background-color: var(--light-300);
  color: var(--text-dark);
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 20px;
  padding: 5px;
  margin-right: 3px;

  &:hover {
    background-color: var(--light-100) !important;
  }

  .icon {
    width: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .icon-angle {
    margin-left: 0;
  }


}


.btn--icon{
  min-height: 34px;
}

.icon {
  display: flex;
  justify-content: center;
  align-items: center;

  &.icon-no-margin {
    margin: var(--space-0);
  }

  img {
    width: 16px;
    height: 16px;
    display: block;
    object-fit: contain;
  }
}

.switch {
  margin-right: 5px;
}

.info {
  padding: 0.25rem 1rem;
  font-size: var(--fs-12);
  color: var(--text-neutral);
  display: flex;
  flex-direction: column;

  .span {
    display: flex;
  }
}

.disabled-li {
  pointer-events: none;

  a {
    opacity: 0.15;
  }
}

</style>
